import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {FormattedMessage} from 'react-intl'
import {
    Text,
    Stack,
    Accordion,
    Box,
    AccordionButton,
    Image,
    AccordionPanel,
    Grid,
    Button,
    Link,
    Flex,
    AccordionItem,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import CloudinaryImage from '../../cloudinary-media/image'
import TruncatedText from '../../truncated-text'
import useTagManager from '../../../hooks/use-tag-manager'
import {
    ProductRatingSummary,
    handleProductReviewsClick,
    shouldPromoteProductRating
} from './product-rating'

const NECKLACE_CLASS_CODES = new Set(['3005', '5005'])

const isNecklaceProduct = (product) => NECKLACE_CLASS_CODES.has(String(product?.c_classCode))

export const ProductDetailsAccordion = ({
    product,
    galleryRef,
    productDetailsRef,
    isSet = false,
    isEngagementProduct,
    onOpenSizeGuide
}) => {
    let accordionConfigJson = null
    const location = useLocation()
    const tagManager = useTagManager()
    const styles = useStyleConfig('ProductDetailsAccordion')
    const ratingStyles = useStyleConfig('ProductRating')
    const shouldHideReviews = isSet || isEngagementProduct || shouldPromoteProductRating(product)

    try {
        accordionConfigJson = JSON.parse(product.c_pdpAccordionConfig)
        if (shouldHideReviews) {
            accordionConfigJson = accordionConfigJson.filter((i) => i.id !== 'reviews')
        }
    } catch (e) {
        console.error('Error parsing PDP Accordion configuration', e)
    }

    useEffect(() => {
        if (location?.hash) {
            const sectionToScroll = location.hash

            if (sectionToScroll) {
                let position
                const domElement = document.querySelector(sectionToScroll)

                if (domElement && galleryRef?.current && productDetailsRef?.current) {
                    const galleryHeight = galleryRef.current?.getBoundingClientRect().height
                    const detailsHeight = productDetailsRef.current?.getBoundingClientRect().height

                    if (galleryHeight > detailsHeight) {
                        position =
                            galleryHeight -
                            domElement?.offsetTop -
                            domElement?.getBoundingClientRect().height -
                            100
                    } else {
                        position = domElement?.offsetTop
                    }

                    setTimeout(() => {
                        window.scrollTo({
                            top: position,
                            behavior: 'smooth'
                        })
                    }, 200)
                }
            }
        }
    }, [])

    if (!accordionConfigJson) {
        return <></>
    }

    accordionConfigJson = accordionConfigJson
        ?.filter((item) => !!item.title)
        ?.filter((section) => {
            // if it has productAttrID → keep only if product has it
            return !section.productAttrID || product?.[section.productAttrID]
        })

    const expandedSections = accordionConfigJson?.reduce((acc, current, currentIndex) => {
        if (current.isExpanded || location?.hash?.indexOf(current.id) !== -1) {
            acc.push(currentIndex)
        }

        return acc
    }, [])

    return (
        <Stack
            gap="0px"
            width="100%"
            className="accordion-stack"
            marginX="auto"
            sx={{
                '.chakra-accordion': {
                    borderColor: 'gray.700',
                    borderTopWidth: '1px'
                },
                'button[disabled].chakra-accordion__button': {
                    cursor: 'default',
                    opacity: 1
                },
                '.accordion-wrapper': {
                    borderColor: 'gray.700'
                },
                '.accordion-wrapper:last-of-type': {
                    borderBottomWidth: '1px'
                },
                '.chakra-accordion__item': {
                    borderWidth: '0px'
                },
                '.chakra-accordion__item:last-of-type': {
                    borderBottomWidth: '0px'
                }
            }}
        >
            <Accordion
                key={`${product?.id}-${shouldHideReviews}`}
                variant="accordion"
                className="accordion"
                allowMultiple={true}
                defaultIndex={expandedSections}
                sx={{
                    '#pro-reviews-tab .chakra-collapse': {
                        display: 'none !important'
                    }
                }}
            >
                {accordionConfigJson.map((item, index) => {
                    const accordionContent = item.productAttrID
                        ? product?.[item.productAttrID]
                        : item.content
                    const showItem = item.id === 'callout' ? product?.c_productPageCallout : true
                    const showSizeGuide =
                        item.id === 'details' &&
                        isNecklaceProduct(product) &&
                        !!product?.c_sizeChart &&
                        typeof onOpenSizeGuide === 'function'

                    return (
                        showItem && (
                            <Box
                                key={item.id}
                                id={`pro-${item.id}-tab`}
                                className="accordion-wrapper"
                                data-index={index}
                                borderBottomWidth="1px"
                            >
                                <AccordionItem
                                    isDisabled={!item.isExpandable && item.id !== 'reviews'}
                                    display="grid"
                                    className="accordion-item"
                                >
                                    {({isExpanded}) => (
                                        <>
                                            {item.id === 'reviews' ? (
                                                <AccordionButton
                                                    id="pdp-rating-summary"
                                                    data-monetate-id="pdp-rating-summary"
                                                    data-rating-placement="reviews"
                                                    padding={0}
                                                    width="100%"
                                                    className="reviews-header"
                                                    sx={ratingStyles.trigger}
                                                    onClick={(event) =>
                                                        handleProductReviewsClick(
                                                            event,
                                                            product,
                                                            tagManager
                                                        )
                                                    }
                                                >
                                                    <Box
                                                        color="black"
                                                        className="accordion-box"
                                                        paddingY="20px"
                                                        justifyContent="space-between"
                                                    >
                                                        <Text
                                                            as="span"
                                                            itemProp="name"
                                                            className="accordion-text"
                                                            fontFamily="heading"
                                                            fontSize={{base: '23px', lg: '26px'}}
                                                            fontWeight="400"
                                                            lineHeight="normal"
                                                            letterSpacing={{
                                                                base: '0.115px',
                                                                lg: '0.13px'
                                                            }}
                                                        >
                                                            {item.title}
                                                            <ProductRatingSummary
                                                                product={product}
                                                            />
                                                        </Text>
                                                    </Box>
                                                </AccordionButton>
                                            ) : (
                                                <Flex alignItems="center" width="100%">
                                                    <AccordionButton
                                                        paddingRight={0}
                                                        className="accordion-button"
                                                        flex="1"
                                                        minWidth="0"
                                                        onClick={() => {
                                                            tagManager.pdpCtaClick(
                                                                `cta click - ${item.title}`,
                                                                product?.name
                                                            )
                                                        }}
                                                    >
                                                        <Text
                                                            as="span"
                                                            itemProp="name"
                                                            className="accordion-text"
                                                            fontFamily={
                                                                item.id === 'description'
                                                                    ? 'caslon'
                                                                    : 'heading'
                                                            }
                                                            fontSize={
                                                                item.id === 'description'
                                                                    ? {base: '24px', lg: '24px'}
                                                                    : {base: '23px', lg: '26px'}
                                                            }
                                                            letterSpacing={
                                                                item.id === 'description'
                                                                    ? {base: '0.12px', lg: '0.12px'}
                                                                    : {
                                                                          base: '0.115px',
                                                                          lg: '0.13px'
                                                                      }
                                                            }
                                                            lineHeight="normal"
                                                            fontWeight={
                                                                item.id === 'description'
                                                                    ? '500'
                                                                    : '400'
                                                            }
                                                        >
                                                            {item.title}
                                                        </Text>

                                                        {item.isExpandable && (
                                                            <>
                                                                {isExpanded ? (
                                                                    <Image
                                                                        alt="Expand Icon"
                                                                        width="15px"
                                                                        height="15px"
                                                                        className="accordion-image"
                                                                        color="black"
                                                                        verticalAlign="middle"
                                                                        src={getUrl(
                                                                            'UIImages/images/minus-large.svg'
                                                                        )}
                                                                    />
                                                                ) : (
                                                                    <Image
                                                                        alt="Expand Icon"
                                                                        width="15px"
                                                                        className="accordion-image"
                                                                        height="15px"
                                                                        color="black"
                                                                        verticalAlign="middle"
                                                                        src={getUrl(
                                                                            'UIImages/images/plus-large.svg'
                                                                        )}
                                                                    />
                                                                )}
                                                            </>
                                                        )}
                                                    </AccordionButton>

                                                    {showSizeGuide && (
                                                        <Button
                                                            onClick={onOpenSizeGuide}
                                                            className="size-guide-button"
                                                            {...styles.sizeGuideButton}
                                                        >
                                                            <Image
                                                                src={getUrl(
                                                                    'UIImages/images/ruler.svg'
                                                                )}
                                                                alt=""
                                                                {...styles.sizeGuideIcon}
                                                            />
                                                            <FormattedMessage
                                                                id="size.guide"
                                                                defaultMessage="Size Guide"
                                                            />
                                                        </Button>
                                                    )}
                                                </Flex>
                                            )}

                                            <AccordionPanel className="accordion-pannel">
                                                <Grid gap="20px" className="accordion-grid">
                                                    {item.imageUrl && (
                                                        <Box
                                                            itemProp="image"
                                                            className="accordion-box"
                                                        >
                                                            <CloudinaryImage
                                                                src={item.imageUrl}
                                                                imgSizes={{
                                                                    base: '100vw',
                                                                    xl: '1100px'
                                                                }}
                                                                sizes={[320, 374, 435, 780, 1100]}
                                                                alt={item.imgAlt}
                                                            />
                                                        </Box>
                                                    )}

                                                    <Stack
                                                        gridArea={'auto'}
                                                        gap="20px"
                                                        className="accordion-grid-stack"
                                                    >
                                                        {item.id === 'description' ||
                                                        item.id === 'details' ? (
                                                            <TruncatedText
                                                                isPanelExpanded={isExpanded}
                                                                className="truncated-text"
                                                                textValue={accordionContent}
                                                                product={product}
                                                                sx={{
                                                                    a: {
                                                                        textDecoration: 'underline',
                                                                        _hover: {
                                                                            textDecoration: 'none',
                                                                            color: 'blue.700'
                                                                        }
                                                                    }
                                                                }}
                                                                linesToShow={
                                                                    item.id === 'description'
                                                                        ? 3
                                                                        : 4
                                                                }
                                                                {...styles.content}
                                                            />
                                                        ) : (
                                                            <Box
                                                                itemProp="text"
                                                                className="accordion-grid-box"
                                                                sx={{
                                                                    a: {
                                                                        textDecoration: 'underline',
                                                                        _hover: {
                                                                            textDecoration: 'none'
                                                                        }
                                                                    }
                                                                }}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: accordionContent
                                                                }}
                                                                {...styles.content}
                                                            />
                                                        )}

                                                        {item.buttonLink && item.buttonText ? (
                                                            <Box className="accordion-grid-button-box">
                                                                <Button
                                                                    as={Link}
                                                                    className="accordion-grid-button"
                                                                    href={item.buttonLink}
                                                                    itemProp="url"
                                                                    variant={'outline'}
                                                                    size="small"
                                                                    paddingLeft="inherit"
                                                                >
                                                                    {item.buttonText}
                                                                </Button>
                                                            </Box>
                                                        ) : null}
                                                    </Stack>
                                                </Grid>
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            </Box>
                        )
                    )
                })}
            </Accordion>
        </Stack>
    )
}

ProductDetailsAccordion.propTypes = {
    product: PropTypes.object,
    isSet: PropTypes.bool,
    galleryRef: PropTypes.object,
    productDetailsRef: PropTypes.object,
    isEngagementProduct: PropTypes.bool,
    onOpenSizeGuide: PropTypes.func
}

export default ProductDetailsAccordion
